import Image from 'next/image';
import { useMemo } from 'react';

import { Badge, KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';

import { PyronStrategy, Trigger } from '../../domain';

interface Props {
  trigger: Trigger;
}

export function PyronTriggerFullDetails({ trigger }: Props) {
  const assetAddress = useMemo(() => {
    return trigger.context?.asset || '';
  }, [trigger.context]);

  const marketAddress = useMemo(() => {
    return trigger.context?.market || '';
  }, [trigger.context]);

  const strategyLabel = useMemo(() => {
    const strategy = trigger.context?.strategy;
    return strategy === PyronStrategy.Lend ? 'Lending' : strategy === PyronStrategy.Borrow ? 'Borrowing' : '-';
  }, [trigger.context]);

  const networkDisplay = useMemo(() => {
    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        <Image src="/assets/webp/solana.webp" alt="Solana" width={20} height={20} />
        <span>Solana</span>
      </div>
    );
  }, []);

  const assetDisplay = useMemo(() => {
    if (!assetAddress) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Asset
        </Text>
        <Text variant="body-sm" className="tw:font-mono">
          {assetAddress}
        </Text>
      </div>
    );
  }, [assetAddress]);

  const marketDisplay = useMemo(() => {
    if (!marketAddress) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Market
        </Text>
        <Text variant="body-sm" className="tw:font-mono">
          {marketAddress}
        </Text>
      </div>
    );
  }, [marketAddress]);

  const strategyDisplay = useMemo(() => {
    if (strategyLabel === '-') return '-';

    const color = strategyLabel === 'Lending' ? 'success' : 'warning';

    return <Badge variant={color}>{strategyLabel}</Badge>;
  }, [strategyLabel]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Asset" value={assetDisplay} />
      <KeyValueRow label="Market" value={marketDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Strategy" value={strategyDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
